'use strict';
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
} from '@mui/material';
import { usePermissionsCatalogQuery } from '@/redux/services/portal/admin.api';
import './AddScopeModal.scss';

const AddPermissionToRoleModal = ({
	open,
	onCancel,
	onConfirm,
	existingPermissionNames,
	isSubmitting,
}) => {
	const [selectedPermissionNames, setSelectedPermissionNames] = useState(new Set());
	const normalizedExisting = useMemo(
		() => new Set(existingPermissionNames.map((name) => (name || '').toLowerCase()).filter(Boolean)),
		[existingPermissionNames],
	);

	useEffect(() => {
		if (open) {
			setSelectedPermissionNames(new Set(existingPermissionNames));
		}
	}, [open, existingPermissionNames]);

	const catalogResult = usePermissionsCatalogQuery(undefined, { skip: !open });

	const results = useMemo(() => {
		const permissions = catalogResult.data || [];
		return [...permissions]
			.filter(Boolean)
			.sort((left, right) => left.localeCompare(right));
	}, [catalogResult.data]);

	const handleTogglePermission = (permission) => {
		if (!permission) return;
		setSelectedPermissionNames((prev) => {
			const next = new Set(prev);
			if (next.has(permission)) {
				next.delete(permission);
			} else {
				next.add(permission);
			}
			return next;
		});
	};

	const selectedNormalized = useMemo(
		() => new Set(
			Array.from(selectedPermissionNames.values())
				.map((name) => (name || '').toLowerCase())
				.filter(Boolean),
		),
		[selectedPermissionNames],
	);
	const permissionsToAdd = useMemo(
		() => results.filter((permission) =>
			selectedPermissionNames.has(permission) && !normalizedExisting.has(permission.toLowerCase()),
		),
		[results, selectedPermissionNames, normalizedExisting],
	);
	const permissionsToRemove = useMemo(
		() => existingPermissionNames.filter((permission) =>
			!selectedNormalized.has((permission || '').toLowerCase()),
		),
		[existingPermissionNames, selectedNormalized],
	);
	const hasChanges = permissionsToAdd.length > 0 || permissionsToRemove.length > 0;
	const updateLabel = hasChanges
		? `Update Permissions (+${permissionsToAdd.length} / -${permissionsToRemove.length})`
		: 'Update Permissions';

	const handleConfirm = () => {
		if (isSubmitting || !hasChanges) return;
		onConfirm({
			selectedPermissionNames: Array.from(selectedPermissionNames.values()),
			permissionsToAdd,
			permissionsToRemove,
		});
	};

	return (
		<Box className="AddScopeModal" sx={{ mt: 1 }}>
			{catalogResult.isFetching ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
					<CircularProgress size={20} />
				</Box>
			) : null}
			{!catalogResult.isFetching ? (
				<List dense className="AddScopeModalList">
					{results.map((permission) => (
						<ListItem
							button
							key={permission}
							selected={selectedPermissionNames.has(permission)}
							onClick={() => {
								if (!isSubmitting) {
									handleTogglePermission(permission);
								}
							}}
						>
							<ListItemIcon>
								<Checkbox
									edge="start"
									checked={selectedPermissionNames.has(permission)}
									tabIndex={-1}
									disableRipple
									disabled={isSubmitting}
								/>
							</ListItemIcon>
							<ListItemText primary={permission} />
						</ListItem>
					))}
					{results.length === 0 ? (
						<ListItem>
							<ListItemText primary="No permissions available" />
						</ListItem>
					) : null}
				</List>
			) : null}
			<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
				<Button onClick={onCancel} sx={{ mr: 1 }}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleConfirm} disabled={!hasChanges || isSubmitting}>
					{isSubmitting ? 'Saving...' : updateLabel}
				</Button>
			</Box>
		</Box>
	);
};

AddPermissionToRoleModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onCancel: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	existingPermissionNames: PropTypes.arrayOf(PropTypes.string),
	isSubmitting: PropTypes.bool,
};

AddPermissionToRoleModal.defaultProps = {
	existingPermissionNames: [],
	isSubmitting: false,
};

export default AddPermissionToRoleModal;
